<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useChatStore } from '../stores/chatStore';

const chatStore = useChatStore();
const config = useRuntimeConfig();
// const { $toast } = useNuxtApp();

const { t } = useI18n();

const props = defineProps({
  isShow: Boolean,
  reviewPattern: Number,
  text: {
    type: String,
    default: '',
  },
  pages: {
    type: Array as PropType<MessageDataPage[]>,
    default: () => [],
  },
  historyId: {
    type: String,
    default: '',
  },
  isGoodReviewed: {
    type: Boolean,
    default: false,
  },
  isBadReviewed: {
    type: Boolean,
    default: false,
  },
  copyText: {
    type: String,
    default: '',
  }
});

let isCopy = ref(false);
let copyTooltipText = ref(t('copy'));

const restartFromBeginning = async () => {
  await chatStore.restartSession({ preserveWindowExtend: true, preserveMessages: true });
};

/**
 * 低評価：モーダルオープン
 */
const openBadReviewModal = async () => {
  if (props.isBadReviewed) {
    //評価キャンセル
    const reviewData = {} as ReviewData;
    reviewData.historyId = props.historyId;

    const result = await chatStore.sendBadReview(reviewData, true);

    if (!result) {
      console.log('評価の送信が失敗しました');
      // $toast.error('評価の送信が失敗しました');
    }
  }
  else {
    chatStore.reviewModal = true;
    chatStore.sendBadReviewHistoryId = props.historyId;
  }
};

/**
 * 高評価：レビュー送信
 */
const sendGoodReview = async () => {
  const result = await chatStore.sendGoodReview(props.historyId, props.isGoodReviewed);

  if (!result) {
    console.log('評価の送信が失敗しました');
    // $toast.error('評価の送信が失敗しました');
  } else {
    console.log('評価の送信が完了しました');
  }
};

/**
 * テキストコピー
 */
const textCopy = () => {
  isCopy.value = true;
  let text = "";

  text = props.copyText;
  if (props.pages.length > 0) {
    text += "\n\n";
    text += "関連情報：\n\n";
    props.pages.forEach((page: MessageDataPage, index) => {
      text += `[${index + 1}] ${page.title}\n${page.url}\n\n`;
    })
  }

  //テキスト中に含まれる<br>を改行文字、<sup>～</sup>を[～]に変換する
  text = text.replace(/<br>/g, "\n").replace(/<sup>(.*?)<\/sup>/g, "[$1]");

  //クリップボードコピー
  copyToClipboard(text);
};

// クリップボードコピー関数
async function copyToClipboard(text: string) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      // Modern API (navigator.clipboard)
      await navigator.clipboard.writeText(text);
      copyTooltipText.value = t('copied');
    } else {
      // Fallback API (document.execCommand)
      const textArea = document.createElement("textarea");
      textArea.value = text;
      // Make the textarea out of viewport
      textArea.style.position = "absolute";
      textArea.style.opacity = "0";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        copyTooltipText.value = t('copied');
      } catch (e) {
        console.log("クリップボードコピー失敗", e);
      } finally {
        document.body.removeChild(textArea);
      }
    }
  } catch (e) {
    console.log("クリップボードコピー失敗", e);
  } finally {
    setTimeout(() => {
      isCopy.value = false;
      copyTooltipText.value = t('copy');
    }, 2000);
  }
}
</script>

<template>
  <div v-if="props.isShow">
    <div v-if="props.reviewPattern == 0">
      <div class="flex mt-4 gap-2 reviewPattern0">
        <!-- コピー -->
        <ClientOnly>
          <VTooltip :delay="10">
            <button type="button" class="" @click="textCopy()" :aria-label="$t('copy')">
              <Icon :name="isCopy ? 'material-symbols:check' : 'material-symbols:content-copy-outline-rounded'"
                size="20px" class="text-gray-500" />
            </button>

            <template #popper>
              {{ copyTooltipText }}
            </template>
          </VTooltip>
        </ClientOnly>

        <!-- 高評価アイコン -->
        <ClientOnly v-if="config.public.reviewPattern == 0">
          <VTooltip :delay="10">
            <button type="button" class="" @click="sendGoodReview" :aria-label="$t('goodAnswer')">
              <Icon name="material-symbols:thumb-up-outline" size="20px"
                :class="{ 'text-design-red': props.isGoodReviewed, 'text-gray-500': !props.isGoodReviewed }" />
            </button>

            <template #popper>
              {{ $t('goodAnswer') }}
            </template>
          </VTooltip>
        </ClientOnly>

        <!-- 低評価アイコン -->
        <ClientOnly v-if="config.public.reviewPattern == 0">
          <VTooltip :delay="10">
            <button type="button" class="" @click="openBadReviewModal" :aria-label="$t('badAnswer')">
              <Icon name="material-symbols:thumb-down-outline" size="20px"
                :class="{ 'text-design-red': props.isBadReviewed, 'text-gray-500': !props.isBadReviewed }" />
            </button>

            <template #popper>
              {{ $t('badAnswer') }}
            </template>
          </VTooltip>
        </ClientOnly>
      </div>
    </div>

    <!-- 別吹き出しで表示 -->
    <div v-else-if="props.reviewPattern == 1">
      <div class="flex mt-4 gap-5 justify-center reviewPattern1">
        <ClientOnly>
          <button type="button" class="text-sm"
            :class="{ 'bg-design-pink': props.isGoodReviewed, 'bg-white': !props.isGoodReviewed }"
            @click="sendGoodReview">{{ $t('reviewFieldGoodAnswer') }}</button>
        </ClientOnly>

        <ClientOnly>
          <button type="button" class="text-sm"
            :class="{ 'bg-design-pink': props.isBadReviewed, 'bg-white': !props.isBadReviewed }"
            @click="openBadReviewModal">{{ $t('reviewFieldBadAnswer') }}</button>
        </ClientOnly>

      </div>

      <div class="flex justify-center mt-3">
        <ClientOnly>
          <button type="button" class="text-sm restart-btn" @click="restartFromBeginning">
            {{ $t('restartFromBeginning') }}
          </button>
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<style scoped>
@reference "../assets/css/tailwind.css";

.reviewPattern0 button {
  @apply rounded-lg;
  @apply w-[30px];
  @apply h-[30px];
  @apply flex;
  @apply items-center;
  @apply justify-center;
}

.reviewPattern0 button:hover {
  @apply bg-gray-100;
}

.reviewPattern1 button {
  @apply rounded-full;
  @apply w-auto;
  @apply px-4;
  @apply py-1;
  @apply border;
  @apply border-design-red;
  @apply text-sm;
}

.reviewPattern1 button:hover {
  @apply hover:bg-design-pink;
}

.restart-btn {
  @apply rounded-full;
  @apply border;
  @apply border-gray-300;
  @apply px-4;
  @apply py-1;
  @apply text-gray-700;
  @apply bg-white;
}

.restart-btn:hover {
  @apply bg-gray-100;
}
</style>
